import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { BadgeCheck, MapPin, Clock, Lock, Building2, Zap, Flame, Globe } from 'lucide-react';
import { mediaUrl } from '@/lib/api';
import { listingPath } from '@/lib/listingUrl';
import { useAuth } from '@/lib/auth';
import FavoriteButton from '@/components/marketplace/FavoriteButton';
import { StarRating } from '@/components/marketplace/StarRating';
import UserAvatar from '@/components/marketplace/UserAvatar';
import CompareCheckbox from '@/components/marketplace/CompareCheckbox';

const typeClass = (t) => {
  if (t === 'sell') return 'indus-badge-sell';
  if (t === 'wanted') return 'indus-badge-wanted';
  if (t === 'service') return 'indus-badge-service';
  return 'indus-badge-rent';
};

const formatPrice = (n) => `₹ ${Number(n).toLocaleString('en-IN')}`;

export const ListingCard = ({ listing }) => {
  const { user } = useAuth();
  // Use the first IMAGE media for the card thumbnail — not media[0], which
  // can be a PDF/video when the seller ordered docs first. Rendering a
  // non-image URL inside <img> is exactly why some cards showed no image
  // while the detail view (which filters to images) displayed it fine.
  const allMedia = listing.media || [];
  const media = allMedia.find((m) => (m.kind || 'image') === 'image');
  const [imgError, setImgError] = useState(false);
  const onRequest = listing.is_price_on_request || (
    !listing.asking_price && !listing.price_amount && !listing.budget_amount && !listing.starting_price_inr
  );
  // For services we show a "Starting from" label so buyers know the number
  // is a floor, not a fixed quote. For products the existing pricing_display
  // (Fixed / Negotiable / Budget / Rent per…) takes precedence.
  const pricingLabel = listing.pricing_display
    || listing.price_type
    || listing.pricing_type
    || listing.budget_type
    || (listing.listing_type === 'service' && listing.starting_price_inr ? 'Starting from' : null)
    || 'Price on Request';
  const displayPrice = onRequest
    ? null
    : (listing.asking_price ?? listing.price_amount ?? listing.budget_amount ?? listing.starting_price_inr ?? null);

  const listingCountry = (listing.country || '').trim();
  const isIntlListing = !!listingCountry && listingCountry.toLowerCase() !== 'india';

  const timeAgo = (() => {
    if (!listing.created_at) return '';
    const diff = Date.now() - new Date(listing.created_at).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    const mo = Math.floor(days / 30);
    return mo <= 1 ? '1 month ago' : `${mo} months ago`;
  })();

  return (
    <Link
      to={listingPath(listing)}
      data-testid='listing-card'
      className='group block'
    >
      <Card className='overflow-hidden hover:shadow-md hover:shadow-black/10 transition-shadow h-full flex flex-col'>
        <div className='relative aspect-[4/3] bg-muted overflow-hidden'>
          {(media?.thumb_url || media?.public_url) && !imgError ? (
            <img
              src={mediaUrl(media.thumb_url || media.public_url)}
              alt={listing.title}
              className='w-full h-full object-contain'
              loading='lazy'
              onError={() => setImgError(true)}
            />
          ) : (
            <div className='w-full h-full flex items-center justify-center text-muted-foreground text-xs bg-secondary'>
              No image
            </div>
          )}
          <div className='absolute top-2 left-2 flex flex-wrap gap-1.5'>
            <span
              className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${typeClass(listing.listing_type)}`}
              data-testid='listing-type-badge'
            >
              {listing.listing_type}
            </span>
            {listing.is_top && (
              <span
                className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-primary text-primary-foreground inline-flex items-center gap-1'
                data-testid='badge-top'
              >
                <Zap className='h-3 w-3' /> Top
              </span>
            )}
            {listing.is_featured && (
              <span
                className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-accent text-accent-foreground'
                data-testid='badge-featured'
              >
                Featured
              </span>
            )}
            {listing.is_urgent && (
              <span
                className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-[hsl(var(--danger))] text-white inline-flex items-center gap-1'
                data-testid='badge-urgent'
              >
                <Flame className='h-3 w-3' /> Urgent
              </span>
            )}
          </div>
          {/* Favorite button — top-right */}
          <div className='absolute top-2 right-2'>
            <FavoriteButton listingId={listing.id} variant='icon' />
          </div>
          {/* Compare shortlist toggle — bottom-left, never navigates */}
          {listing.status !== 'sold' && (
            <div className='absolute bottom-2 left-2'>
              <CompareCheckbox listing={listing} />
            </div>
          )}
          {/* SOLD ribbon — diagonal overlay for closed listings still in the
              30-day social-proof window. Kept above image but below badges
              in stacking order so it doesn't cover the type/featured badges. */}
          {listing.status === 'sold' && (
            <div
              className='pointer-events-none absolute inset-0 flex items-center justify-center'
              data-testid='listing-card-sold-overlay'
            >
              <div className='bg-emerald-600/90 text-white text-sm sm:text-base font-bold tracking-wider px-6 py-1.5 rotate-[-8deg] shadow-lg uppercase'>
                {listing.sold_closure_label === 'fulfilled' ? 'Fulfilled' : 'Sold'}
              </div>
            </div>
          )}
        </div>
        <div className='p-4 flex-1 flex flex-col gap-2'>
          <div className='flex items-start justify-between gap-2'>
            <h3 className='font-semibold leading-snug line-clamp-2 group-hover:underline underline-offset-4'>
              {listing.title}
            </h3>
            {listing.is_gst_verified && (
              <span
                className='inline-flex items-center gap-1 text-[10px] font-medium text-[hsl(var(--verified))] shrink-0 rounded-full bg-[hsl(var(--verified))]/10 px-1.5 py-0.5'
                data-testid='badge-gst-verified'
                title='Seller has been GST verified by IndusFact admin'
              >
                <BadgeCheck className='h-3 w-3' /> GST Verified
              </span>
            )}
          </div>
          <div className='flex flex-wrap gap-1 text-xs'>
            {listing.subcategory_name && (
              <span className='rounded-full bg-secondary px-2 py-0.5'>{listing.subcategory_name}</span>
            )}
            {listing.listing_type !== 'service' && listing.condition && (
              <span className='rounded-full bg-secondary px-2 py-0.5'>{listing.condition}</span>
            )}
            {listing.listing_type !== 'service' && listing.brand && (
              <span className='rounded-full bg-secondary px-2 py-0.5'>{listing.brand}</span>
            )}
            {listing.listing_type === 'service' && listing.experience_years != null && (
              <span
                className='rounded-full bg-secondary px-2 py-0.5'
                data-testid={`listing-card-experience-${listing.id}`}
              >
                {listing.experience_years}+ yrs exp
              </span>
            )}
            {listing.listing_type === 'service' && listing.emergency_service && listing.emergency_service !== 'no' && (
              <span
                className='rounded-full bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 font-semibold'
                data-testid={`listing-card-emergency-${listing.id}`}
                title='Emergency service available'
              >
                {listing.emergency_service === '24x7' ? '24×7' : 'Emergency'}
              </span>
            )}
            {listing.listing_type === 'service' && listing.oem_authorized && (
              <span
                className='rounded-full bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 font-semibold'
                data-testid={`listing-card-oem-${listing.id}`}
                title={listing.authorized_brand ? `OEM-authorized for ${listing.authorized_brand}` : 'OEM Authorized'}
              >
                OEM Authorized{listing.authorized_brand ? ` · ${listing.authorized_brand}` : ''}
              </span>
            )}
            {listing.listing_type === 'service' && listing.response_time && (
              <span
                className='rounded-full bg-secondary px-2 py-0.5'
                data-testid={`listing-card-response-${listing.id}`}
              >
                {listing.response_time === 'same_day' ? 'Same-day'
                 : listing.response_time === '24h' ? '24h response'
                 : listing.response_time === '48h' ? '48h response'
                 : 'Within a week'}
              </span>
            )}
          </div>
          {user && listing.company_name_snapshot && (
            <div
              className='flex items-center gap-1.5 text-xs text-muted-foreground'
              data-testid='listing-card-company'
            >
              {listing.seller_photo_thumb ? (
                <UserAvatar
                  name={listing.company_name_snapshot}
                  photo={listing.seller_photo_thumb}
                  size={18}
                  className='shrink-0'
                />
              ) : (
                <Building2 className='h-3 w-3 shrink-0' />
              )}
              <span className='truncate'>{listing.company_name_snapshot}</span>
              {listing.seller_review_count > 0 && listing.seller_avg_rating != null && (
                <StarRating
                  value={listing.seller_avg_rating}
                  size={3}
                  className='shrink-0'
                />
              )}
            </div>
          )}
          <div className='mt-auto flex flex-col gap-2'>
            <div className='flex items-center justify-between text-sm gap-2'>
              <div className='flex items-center gap-1 text-muted-foreground min-w-0'>
                <MapPin className='h-3.5 w-3.5 shrink-0' />
                <span className='truncate'>{[listing.city, listing.state, isIntlListing ? listingCountry : null].filter(Boolean).join(', ')}</span>
                {isIntlListing && (
                  <span
                    className='shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-medium'
                    data-testid='listing-card-intl-badge'
                    title={`International · Located in ${listingCountry}`}
                  >
                    <Globe className='h-3 w-3' /> Intl
                  </span>
                )}
              </div>
              <div className='text-right'>
                {displayPrice != null ? (
                  <>
                    <div className='font-semibold tabular-nums' data-testid='listing-card-price'>{formatPrice(displayPrice)}</div>
                    {pricingLabel && (
                      <div className='text-[10px] text-muted-foreground'>{pricingLabel}</div>
                    )}
                  </>
                ) : (
                  <div
                    className='text-sm font-medium text-foreground/80'
                    data-testid='listing-card-price-on-request'
                  >
                    Price on Request
                  </div>
                )}
              </div>
            </div>
            <div className='flex items-center justify-between text-xs text-muted-foreground'>
              <div className='flex items-center gap-1'>
                <Clock className='h-3 w-3' /> {timeAgo}
              </div>
              {!user ? (
                <div className='flex items-center gap-1' data-testid='listing-card-masked-contact-chip'>
                  <Lock className='h-3 w-3' /> Login to view contact
                </div>
              ) : (
                <div className='flex items-center gap-1'>
                  <Lock className='h-3 w-3' /> Reveal contact
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default ListingCard;
